/**
 * Plugin Cache System
 *
 * Provides in-memory caching with TTL support for plugin data.
 * Can be extended with Redis adapter for production use.
 *
 * Features:
 * - TTL (Time To Live) support
 * - Automatic cleanup of expired items
 * - Type-safe get/set operations
 * - Memory-efficient storage
 */

import { PluginCache as IPluginCache } from '@/lib/platform/types/plugins';

import { pluginLogger } from '@/lib/platform/utils/logger';

interface CacheEntry<T> {
  value: T;
  expires: number; // Timestamp in milliseconds
}

export class InMemoryPluginCache implements IPluginCache {
  private cache = new Map<string, CacheEntry<unknown>>();
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor(cleanupIntervalMs: number = 60000) {
    // Start automatic cleanup every minute
    this.startCleanup(cleanupIntervalMs);
  }

  /**
   * Get value from cache
   * Returns null if key doesn't exist or has expired
   */
  async get<T>(key: string): Promise<T | null> {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if expired
    if (Date.now() > entry.expires) {
      this.cache.delete(key);
      return null;
    }

    return entry.value as T;
  }

  /**
   * Set value in cache with optional TTL
   * @param key Cache key
   * @param value Value to cache
   * @param ttlSeconds Time to live in seconds (default: 3600 = 1 hour)
   */
  async set<T>(key: string, value: T, ttlSeconds: number = 3600): Promise<void> {
    const expires = Date.now() + ttlSeconds * 1000;

    this.cache.set(key, {
      value,
      expires,
    });
  }

  /**
   * Delete value from cache
   */
  async delete(key: string): Promise<void> {
    this.cache.delete(key);
  }

  /**
   * Clear all cached values
   */
  async clear(): Promise<void> {
    this.cache.clear();
  }

  /**
   * Get cache size (number of entries)
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Get all cache keys
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Check if key exists and is not expired
   */
  async has(key: string): Promise<boolean> {
    const value = await this.get(key);
    return value !== null;
  }

  /**
   * Start automatic cleanup of expired entries
   */
  private startCleanup(intervalMs: number): void {
    if (this.cleanupInterval) {
      return;
    }

    this.cleanupInterval = setInterval(() => {
      this.cleanupExpired();
    }, intervalMs);

    // Don't prevent Node.js from exiting
    if (this.cleanupInterval.unref) {
      this.cleanupInterval.unref();
    }
  }

  /**
   * Stop automatic cleanup
   */
  stopCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Manually cleanup expired entries
   */
  private cleanupExpired(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expires) {
        keysToDelete.push(key);
      }
    }

    for (const key of keysToDelete) {
      this.cache.delete(key);
    }

    if (keysToDelete.length > 0) {
      pluginLogger.log(`[PluginCache] Cleaned up ${keysToDelete.length} expired entries`);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    totalEntries: number;
    expiredEntries: number;
    validEntries: number;
  } {
    const now = Date.now();
    let expired = 0;
    let valid = 0;

    for (const entry of this.cache.values()) {
      if (now > entry.expires) {
        expired++;
      } else {
        valid++;
      }
    }

    return {
      totalEntries: this.cache.size,
      expiredEntries: expired,
      validEntries: valid,
    };
  }

  /**
   * Cleanup on destroy
   */
  destroy(): void {
    this.stopCleanup();
    this.cache.clear();
  }
}

// ============================================================================
// Redis Cache Adapter (for future production use)
// ============================================================================

/**
 * Redis-based cache implementation for production
 *
 * ⚠️ WARNING: This is a future feature stub - NOT YET IMPLEMENTED!
 *
 * The Redis adapter is planned for production use when distributed caching
 * is required across multiple server instances. For now, use InMemoryPluginCache.
 *
 * To implement:
 * 1. Install redis package: npm install redis @types/redis
 * 2. Implement connection pooling
 * 3. Add Redis GET/SET/DEL operations with TTL
 * 4. Add error handling and retry logic
 * 5. Add monitoring and health checks
 *
 * @see https://redis.io/docs/clients/nodejs/
 * @deprecated Use InMemoryPluginCache instead (default)
 */
export class RedisPluginCache implements IPluginCache {
  // private redis: Redis;

  constructor(/* redis connection */) {
    throw new Error(
      'RedisPluginCache is not implemented yet. Use InMemoryPluginCache instead (default). ' +
        'See lib/platform/plugins/core/PluginCache.ts for implementation guide.'
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async get<T>(_key: string): Promise<T | null> {
    throw new Error('RedisPluginCache.get() is not implemented');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async set<T>(_key: string, _value: T, _ttlSeconds?: number): Promise<void> {
    throw new Error('RedisPluginCache.set() is not implemented');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async delete(_key: string): Promise<void> {
    throw new Error('RedisPluginCache.delete() is not implemented');
  }

  async clear(): Promise<void> {
    throw new Error('RedisPluginCache.clear() is not implemented');
  }
}

// ============================================================================
// Factory
// ============================================================================

export function createPluginCache(type: 'memory' | 'redis' = 'memory'): IPluginCache {
  switch (type) {
    case 'memory':
      return new InMemoryPluginCache();
    case 'redis':
      return new RedisPluginCache();
    default:
      return new InMemoryPluginCache();
  }
}
